import { ChangeDetectorRef, Directive, input, signal } from "@angular/core";
import { ObjectOccurrence } from "../data.ingestion.service";

/** Saved search row from `s25-ng-search-dropdown` (location / resource). */
export interface SearchItem {
    query_id?: number;
    query?: string;
    val?: number | string;
    query_name?: string;
    [key: string]: unknown;
}

/** Shared layout between location and resource occurrence search UIs. */
export const OBJECT_OCCURRENCE_SEARCH_LAYOUT_STYLES = `
        .search-controls {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 0.5rem;
        }
        .search-method-selector {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.35rem;
        }
        .search-method-label {
            font-weight: 600;
        }
        .text-search-input-group {
            display: flex;
            gap: 0.5rem;
        }
        .text-search-input {
            flex: 1;
        }
        .search-results-list {
            list-style: none;
            padding: 0;
            margin: 0;
        }
        .search-result-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem;
            margin-bottom: 0.35rem;
            background: #fff;
            border: 1px solid #ddd;
            border-radius: 4px;
        }
        .search-loading,
        .no-results {
            font-size: 0.9rem;
            color: #666;
        }
        h5 {
            margin: 0.5rem 0;
            font-size: 0.95rem;
        }
`;

/**
 * Saved-search vs name-search flow shared by object-occurrence location and resource pickers.
 */
@Directive()
export abstract class ObjectOccurrenceSearchBase {
    protected constructor(protected readonly cd: ChangeDetectorRef) {}

    readonly occurrenceUuid = input.required<string>();
    readonly occurrences = input.required<ObjectOccurrence[]>();
    readonly readOnly = input<boolean>(false);
    readonly totalOccurrences = input<number>(0);
    readonly existingObjectIds = input.required<number[]>();

    readonly nameSearchText = signal("");
    readonly selectedSearch = signal<SearchItem | undefined>(undefined);
    readonly searching = signal(false);
    readonly hasSearched = signal(false);
    readonly searchMethod = signal<"saved" | "name">("saved");

    onSearchMethodChange(method: "saved" | "name"): void {
        this.searchMethod.set(method);
        if (method === "saved") {
            this.nameSearchText.set("");
        } else {
            this.clearSearchSelection();
        }
    }

    readonly clearSearchSelection = (): void => {
        this.selectedSearch.set(undefined);
        this.nameSearchText.set("");
        this.resetSearchResults();
        this.hasSearched.set(false);
    };

    readonly onSearchSelected = async (search: SearchItem): Promise<void> => {
        if (!search) {
            return;
        }
        this.nameSearchText.set("");
        this.selectedSearch.set(search);

        let searchQuery = String(search.query_id || search.query || search.val || "");
        if (!searchQuery.startsWith("&")) {
            searchQuery = `&query_id=${searchQuery}`;
        }
        searchQuery = this.rewriteSavedSearchQuery(searchQuery);
        await this.runSearch(searchQuery);
    };

    readonly searchByName = async (): Promise<void> => {
        const trimmed = this.nameSearchText().trim();
        if (!trimmed) {
            alert(this.emptyNameSearchAlert());
            return;
        }
        await this.runSearch(this.buildNameSearchQuery(trimmed));
    };

    protected abstract resetSearchResults(): void;

    /** Normalize saved-search query prefix (e.g. `&spaces_` → `&` for locations). */
    protected abstract rewriteSavedSearchQuery(searchQuery: string): string;

    protected abstract runSearch(searchQuery: string): Promise<void>;

    protected abstract emptyNameSearchAlert(): string;

    protected abstract buildNameSearchQuery(trimmedName: string): string;
}
